/**
 * Persistence (doc 12 §Saves, doc 18 §4). localStorage, versioned, and keyed by
 * slot from the first line of code — retrofitting slots onto a single key is the
 * expensive kind of change, and a fresh slot is how onboarding gets play-tested
 * without wiping the tester's own colony.
 *
 * Writes are atomic: serialise → tmp → read back → promote, keeping exactly one
 * previous generation. A crash mid-write can lose the newest payday, never the
 * profile. Nothing here is allowed to throw at the caller: storage can be full,
 * disabled, or full of yesterday's garbage, and the game still boots.
 */

import { migrate, newProfile } from '../meta/profile.ts';
import type { Profile } from '../meta/types.ts';

export const SLOTS = ['s1', 's2', 's3'] as const;
export type SlotId = (typeof SLOTS)[number];

/**
 * The STORAGE format version — how the keys are laid out — and deliberately NOT
 * the profile's `SCHEMA_VERSION`. Those are different things: a profile schema
 * bump is exactly the case `migrate` exists to handle, and if the key moved with
 * it, every bump would orphan every existing colony instead of upgrading it.
 * This only changes if the key LAYOUT changes, and then with a copy-forward.
 */
const PREFIX = 'ki_save_v1';
const LAST_SLOT_KEY = `${PREFIX}:last`;

/** The two methods we need — a Map stands in for storage in tests. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SlotInfo {
  id: SlotId;
  profile: Profile | null;
  /** The live copy failed to parse and the previous generation was used. */
  recovered: boolean;
}

function keyFor(slot: SlotId, gen: '' | 'prev' | 'tmp' = ''): string {
  return gen === '' ? `${PREFIX}:${slot}` : `${PREFIX}:${slot}:${gen}`;
}

export class SaveStore {
  private readonly storage: StorageLike;
  private readonly now: () => number;

  constructor(storage: StorageLike, now: () => number = () => Date.now()) {
    this.storage = storage;
    this.now = now;
  }

  private read(key: string): Profile | null {
    let raw: string | null;
    try {
      raw = this.storage.getItem(key);
    } catch {
      return null; // storage disabled entirely (private mode, blocked cookies)
    }
    if (raw === null || raw === '') return null;
    try {
      return migrate(JSON.parse(raw) as unknown, this.now());
    } catch {
      return null;
    }
  }

  /** Load a slot, falling back to the previous generation if the live copy is junk. */
  loadSlot(slot: SlotId): SlotInfo {
    const live = this.read(keyFor(slot));
    if (live) return { id: slot, profile: live, recovered: false };
    const prev = this.read(keyFor(slot, 'prev'));
    if (prev) return { id: slot, profile: prev, recovered: true };
    return { id: slot, profile: null, recovered: false };
  }

  list(): SlotInfo[] {
    return SLOTS.map((s) => this.loadSlot(s));
  }

  /** Returns null on success, or a reason the write failed (shown as a toast). */
  save(slot: SlotId, profile: Profile): string | null {
    let json: string;
    try {
      json = JSON.stringify(profile);
      JSON.parse(json); // verify before anything touches the live key
    } catch {
      return 'SAVE REFUSED — THE PROFILE WOULD NOT SERIALISE';
    }
    try {
      const tmp = keyFor(slot, 'tmp');
      this.storage.setItem(tmp, json);
      if (this.storage.getItem(tmp) !== json) {
        this.storage.removeItem(tmp);
        return 'SAVE REFUSED — STORAGE TRUNCATED THE WRITE';
      }
      const live = this.storage.getItem(keyFor(slot));
      if (live !== null) this.storage.setItem(keyFor(slot, 'prev'), live);
      this.storage.setItem(keyFor(slot), json);
      this.storage.removeItem(tmp);
      this.storage.setItem(LAST_SLOT_KEY, slot);
      return null;
    } catch {
      return 'SAVE FAILED — STORAGE IS FULL OR BLOCKED';
    }
  }

  create(slot: SlotId, name: string): Profile {
    const p = newProfile(name, this.now());
    this.save(slot, p);
    return p;
  }

  erase(slot: SlotId): void {
    for (const gen of ['', 'prev', 'tmp'] as const) {
      try {
        this.storage.removeItem(keyFor(slot, gen));
      } catch {
        // a slot that refuses to be deleted is not worth crashing over
      }
    }
  }

  lastSlot(): SlotId | null {
    try {
      const v = this.storage.getItem(LAST_SLOT_KEY);
      return SLOTS.includes(v as SlotId) ? (v as SlotId) : null;
    } catch {
      return null;
    }
  }

  /** Doc 18: "copy your fish to another computer". */
  exportSlot(slot: SlotId): string | null {
    const info = this.loadSlot(slot);
    return info.profile ? JSON.stringify(info.profile, null, 2) : null;
  }

  /** Returns null on success, or why the paste was rejected. */
  importSlot(slot: SlotId, json: string): string | null {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      return 'THAT IS NOT A SAVE FILE';
    }
    const profile = migrate(parsed, this.now());
    if (!profile) return 'THAT SAVE IS FROM A NEWER VERSION';
    return this.save(slot, profile);
  }
}

/** Browser storage, or a memory shim when the browser refuses to provide one. */
export function browserStorage(): StorageLike {
  try {
    const probe = '__ki_probe';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    const mem = new Map<string, string>();
    return {
      getItem: (k) => mem.get(k) ?? null,
      setItem: (k, v) => void mem.set(k, v),
      removeItem: (k) => void mem.delete(k),
    };
  }
}
